import { API_ENDPOINTS, API_HEADERS } from "../constants/api";
import { ChatMessage, ChatTurnResult, StreamEvent } from "../types/chatbot";

const buildPayload = (history: ChatMessage[], userMessage: string) => ({
  history: history.map(({ role, content }) => ({ role, content })),
  user_message: userMessage,
});

const handleHttpError = (status: number): never => {
  if (status === 429) {
    throw new Error(
      "You are sending messages too quickly. Please try again in a moment."
    );
  }
  throw new Error(`Chat request failed: ${status}`);
};

/**
 * Sends a chat turn to the AILH backend and returns the grounded reply.
 * Contract: chatbot-spec.md §2.1.
 */
export const sendMessage = async (
  history: ChatMessage[],
  userMessage: string
): Promise<ChatTurnResult> => {
  const res = await fetch(API_ENDPOINTS.chatbotMessage, {
    method: "POST",
    headers: API_HEADERS,
    body: JSON.stringify(buildPayload(history, userMessage)),
  });

  if (!res.ok) handleHttpError(res.status);
  return (await res.json()) as ChatTurnResult;
};

/**
 * Streams a chat turn via SSE. Calls `onEvent` for each event the server emits.
 * Contract: chatbot-spec.md §2.2.
 */
export const streamMessage = async (
  history: ChatMessage[],
  userMessage: string,
  onEvent: (event: StreamEvent) => void,
  signal?: AbortSignal
): Promise<void> => {
  const res = await fetch(API_ENDPOINTS.chatbotStream, {
    method: "POST",
    headers: API_HEADERS,
    body: JSON.stringify(buildPayload(history, userMessage)),
    signal,
  });

  if (!res.ok) handleHttpError(res.status);
  if (!res.body) throw new Error("Stream response has no body");

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let sep = buffer.indexOf("\n\n");
      while (sep !== -1) {
        const rawEvent = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const parsed = parseSseBlock(rawEvent);
        if (parsed) onEvent(parsed);
        sep = buffer.indexOf("\n\n");
      }
    }
  } finally {
    reader.releaseLock();
  }
};

const parseSseBlock = (block: string): StreamEvent | null => {
  let event = "";
  const dataLines: string[] = [];
  for (const line of block.split("\n")) {
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trim());
    }
  }
  if (!event) return null;
  const dataString = dataLines.join("\n");
  let payload: any = {};
  if (dataString) {
    try {
      payload = JSON.parse(dataString);
    } catch {
      return null;
    }
  }
  switch (event) {
    case "retrieved":
      return { type: "retrieved", retrieved: payload.retrieved ?? [] };
    case "token":
      return { type: "token", delta: payload.delta ?? "" };
    case "reasoning":
      return { type: "reasoning", reasoning: payload.reasoning ?? [] };
    case "error":
      return { type: "error", message: payload.message ?? "Stream error" };
    case "done":
      return { type: "done" };
    default:
      return null;
  }
};
